import { CurrencyIsoCode } from "@/models/GameResell";
import { GameItem } from "@/models/orders/OrderModelV2";
import { formatMoney } from "@/utils/CommonUtils";
import Image from "next/image";
import { DeviceIcons } from "@/components/compatibility/DeviceIcons";
import { DeviceType } from "@/services/ContentsService";
import { Badge } from "@/v2-components/ui/badge";
import useUser from "@/api/apis";
import { useTrans } from "@/utils/useTrans";

export function OrderSummaryGameItems({
  gameItems,
  currency,
  countryCode,
  header,
}: {
  header?: string;
  gameItems: GameItem[];
  currency: CurrencyIsoCode;
  countryCode: string;
}) {
  const { user } = useUser();

  return (
    <section className="space-y-4">
      {!!header && <h2 className="text-lg font-400">{header}</h2>}
      <ul>
        {gameItems.map((game, i) => {
          return (
            <li key={i} className="flex justify-between py-2.5">
              {game.bundled ? (
                <OrderSummaryBundleGameItem item={game} />
              ) : (
                <OrderSummaryGameItem
                  loggedIn={!!user}
                  currency={currency}
                  countryCode={countryCode}
                  item={game}
                />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function OrderSummaryGameItem({
  item,
  currency,
  countryCode,
  loggedIn,
}: {
  loggedIn: boolean;
  item: GameItem;
  currency: CurrencyIsoCode;
  countryCode: string;
}) {
  const { t } = useTrans(["gnb"]);
  const { discountPercentile: percent } = item;
  const discount = formatMoney({
    amount: item.price,
    currency,
    countryCode,
  });
  const price = formatMoney({
    amount: item.price - item.discount,
    currency,
    countryCode,
  });

  const discountApplied = t("{{percent}}% Off Applied", {
    percent,
    defaultValue: `${percent}% Off Applied`,
  });
  const discountCoupon = t("{{percent}}% Off Coupon", {
    percent,
    defaultValue: `${percent}% Off Coupon`,
  });
  const text = loggedIn ? discountApplied : discountCoupon;

  return (
    <div className="flex h-[100px] w-full items-center">
      <div className="relative min-w-[84px] laptop:min-w-[100px]">
        <div className="relative mx-auto aspect-[0.68] w-full max-w-[58px] laptop:max-w-[68px]">
          <Image
            src={item.thumbnail}
            alt={item.name}
            className="rounded-md object-cover"
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-1 text-base">
        <p className="line-clamp-2 break-all text-base font-600 laptop:text-lg">
          {item.name}
        </p>
        <p className="text-base capitalize">{item.platform}</p>
        <DeviceIcons
          devices={item.compatibleDevices as DeviceType[]}
          className="flex grow gap-x-1.5"
        />
      </div>
      <div className="ml-4 flex flex-col items-end gap-1">
        {percent > 0 && (
          <Badge size="sm" variant={loggedIn ? "new" : "sale"}>
            {text}
          </Badge>
        )}
        {item.discount > 0 && (
          <p className="text-base text-coolgray-50 line-through laptop:text-lg">
            {discount}
          </p>
        )}
        {item.price - item.discount > 0 && (
          <p className="text-base font-600 laptop:text-lg">{price}</p>
        )}
      </div>
    </div>
  );
}

export function OrderSummaryBundleGameItem({ item }: { item: GameItem }) {
  const { t } = useTrans(["gnb"]);

  return (
    <div className="flex h-[100px] w-full items-center">
      <div className="relative min-w-[84px] laptop:min-w-[100px]">
        <div className="relative mx-auto aspect-[0.68] w-full max-w-[58px] laptop:max-w-[68px]">
          <Image
            src={item.thumbnail}
            alt={item.name}
            className="rounded-md object-cover"
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-1 text-base">
        <p className="line-clamp-2 break-all text-base font-600 laptop:text-lg">
          {item.name}
        </p>
        <p className="text-base capitalize">{item.platform}</p>
        <DeviceIcons
          devices={item.compatibleDevices as DeviceType[]}
          className="flex grow gap-x-1.5"
        />
      </div>
      <div className="ml-4 flex flex-col items-end gap-1">
        <Badge size="sm" variant={"new"}>
          {t("Bundled Game")}
        </Badge>
      </div>
    </div>
  );
}
